import { Pause, Play } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { api } from '../../api';
import { t } from '../../i18n';
import type { PrayerTime, RecordIntent } from '../../types';
import { Button } from '../shared/Button';
import { useToast } from '../shared/Toast';

type ReviewBase = {
  blob: Blob;
  blob_url: string;
  duration_seconds: number;
  mime: string;
  ext: 'webm' | 'm4a';
  intent: RecordIntent;
  surah: { number: number; name_uz: string; name_ar: string } | null;
};

type ReviewState = ReviewBase &
  (
    | { module: 'recitation'; prayer_time: PrayerTime }
    | { module: 'memorization'; slot: string; ayah_from: number; ayah_to: number }
  );

function fmtTimer(sec: number): string {
  const m = Math.floor(sec / 60);
  const s = Math.floor(sec % 60);
  return `${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
}

export function Review() {
  const navigate = useNavigate();
  const location = useLocation();
  const toast = useToast();
  const stateData = location.state as ReviewState | null;

  const audioRef = useRef<HTMLAudioElement | null>(null);
  const [playing, setPlaying] = useState(false);
  const [position, setPosition] = useState(0);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!stateData) {
      navigate('/child', { replace: true });
    }
  }, [stateData, navigate]);

  const releaseUrl = () => {
    if (stateData) {
      try {
        URL.revokeObjectURL(stateData.blob_url);
      } catch {
        // ignore
      }
    }
  };

  if (!stateData) return null;

  const onPlayPause = () => {
    const a = audioRef.current;
    if (!a) return;
    if (playing) a.pause();
    else void a.play();
  };

  const onSeek = (e: React.ChangeEvent<HTMLInputElement>) => {
    const a = audioRef.current;
    if (!a) return;
    a.currentTime = Number(e.target.value);
    setPosition(a.currentTime);
  };

  const save = async () => {
    setSubmitting(true);
    try {
      const fd = new FormData();
      fd.append('audio', stateData.blob, `recording.${stateData.ext}`);
      fd.append('duration_seconds', String(stateData.duration_seconds));
      fd.append('mime_type', stateData.mime.split(';')[0] ?? stateData.mime);
      if (stateData.module === 'memorization') {
        fd.append('slot_key', stateData.slot);
        await api.uploadMemorization(fd);
      } else {
        fd.append('prayer_time', stateData.prayer_time);
        fd.append('surah_number', String(stateData.surah?.number ?? 1));
        await api.uploadRecitation(fd);
      }
      releaseUrl();
      toast.show(t('review.save'), 'success');
      navigate('/child', { replace: true });
    } catch (err) {
      toast.show(err instanceof Error ? err.message : t('common.error.network'), 'danger');
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-dvh bg-record-bg text-record-fg font-sans">
      <main className="mx-auto flex min-h-dvh max-w-md flex-col gap-8 p-6">
        <h1 className="mt-4 text-center text-[18px] font-medium">{t('review.title')}</h1>

        <div className="flex flex-col items-center gap-5">
          <button
            onClick={onPlayPause}
            disabled={submitting}
            aria-label={playing ? 'pause' : 'play'}
            className="flex h-[80px] w-[80px] items-center justify-center rounded-full bg-record-fg/15 ring-1 ring-record-fg/20 active:scale-[0.97]"
          >
            {playing ? <Pause size={32} /> : <Play size={32} className="ml-1" />}
          </button>

          <div className="w-full">
            <input
              type="range"
              min={0}
              max={stateData.duration_seconds}
              step={0.1}
              value={position}
              onChange={onSeek}
              className="w-full accent-record-accent"
            />
            <div className="mt-1 flex justify-between text-[11px] text-record-fg/70 font-mono tabular-nums">
              <span>{fmtTimer(position)}</span>
              <span>{fmtTimer(stateData.duration_seconds)}</span>
            </div>
          </div>
        </div>

        <audio
          ref={audioRef}
          src={stateData.blob_url}
          onPlay={() => setPlaying(true)}
          onPause={() => setPlaying(false)}
          onTimeUpdate={(e) => setPosition(e.currentTarget.currentTime)}
          onEnded={() => {
            setPlaying(false);
            setPosition(0);
          }}
          preload="metadata"
        />

        <div className="mt-auto flex flex-col gap-3">
          <button
            disabled={submitting}
            onClick={save}
            className="h-12 rounded-md bg-record-fg text-record-bg text-[15px] font-medium transition active:scale-[0.98] disabled:opacity-40"
          >
            {t('review.save')}
          </button>
          <Button
            variant="secondary"
            disabled={submitting}
            onClick={() => {
              releaseUrl();
              navigate('/child/record', { replace: true, state: stateData.intent });
            }}
            className="!border-record-fg/30 !text-record-fg"
          >
            {t('review.rerecord')}
          </Button>
          <Button
            variant="ghost"
            disabled={submitting}
            onClick={() => {
              releaseUrl();
              navigate('/child', { replace: true });
            }}
            className="!text-record-fg/70"
          >
            {t('common.cancel')}
          </Button>
        </div>
      </main>
    </div>
  );
}
